import React, { useState, useEffect } from "react";
import { X, Eye, EyeOff, Check, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { setSession } from "../utils/session";

const FORMSUBMIT_EMAIL = "putonclothingtech@gmail.com";

/* ── Password strength scoring ── */
function getStrength(pwd) {
	if (!pwd) return { score: 0, label: "", color: "" };
	const checks = {
		length: pwd.length >= 8,
		uppercase: /[A-Z]/.test(pwd),
		lowercase: /[a-z]/.test(pwd),
		number: /[0-9]/.test(pwd),
		special: /[@$!%*?&#^()_\-+=[\]{};:'",.<>/\\|`~]/.test(pwd),
	};
	const passed = Object.values(checks).filter(Boolean).length;

	if (passed <= 1) return { score: 1, label: "Weak", color: "#EF4444", checks };
	if (passed === 2)
		return { score: 2, label: "Fair", color: "#F59E0B", checks };
	if (passed === 3)
		return { score: 3, label: "Good", color: "#3B82F6", checks };
	if (passed === 4)
		return { score: 4, label: "Strong", color: "#10B981", checks };
	return { score: 5, label: "Very Strong", color: "#059669", checks };
}

const RULES = [
	{ key: "length", label: "At least 8 characters" },
	{ key: "uppercase", label: "One uppercase letter (A–Z)" },
	{ key: "lowercase", label: "One lowercase letter (a–z)" },
	{ key: "number", label: "One number (0–9)" },
	{ key: "special", label: "One special character (!@#$…)" },
];

export default function TryNowModal({ onClose }) {
	const navigate = useNavigate();

	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [confirm, setConfirm] = useState("");

	const [showPwd, setShowPwd] = useState(false);
	const [showConf, setShowConf] = useState(false);
	const [touched, setTouched] = useState({
		email: false,
		password: false,
		confirm: false,
	});

	const [status, setStatus] = useState("idle"); // idle | sending | success
	const [serverError, setServerError] = useState("");

	const strength = getStrength(password);

	/* close on Escape */
	useEffect(() => {
		const h = (e) => {
			if (e.key === "Escape") onClose();
		};
		window.addEventListener("keydown", h);
		return () => window.removeEventListener("keydown", h);
	}, [onClose]);

	/* ── Validation ── */
	const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
	const passwordOk = strength.score >= 4; // all 5 checks pass
	const confirmOk = confirm === password && confirm !== "";
	const showConfirm = email.trim() !== "" && password !== "";

	const emailErr =
		touched.email && !emailValid ? "Enter a valid email address." : "";
	const passwordErr =
		touched.password && !passwordOk
			? "Password does not meet all requirements below."
			: "";
	const confirmErr =
		touched.confirm && !confirmOk
			? confirm === ""
				? "Please confirm your password."
				: "Passwords do not match."
			: "";

	const canSubmit =
		emailValid && passwordOk && confirmOk && status !== "sending";

	async function handleSubmit(e) {
		e.preventDefault();
		setTouched({ email: true, password: true, confirm: true });
		if (!canSubmit) return;

		setServerError("");
		setStatus("sending");
		try {
			const res = await fetch(
				`https://formsubmit.co/ajax/${FORMSUBMIT_EMAIL}`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
					},
					body: JSON.stringify({
						_subject: "PutOn AI — New Sign-Up",
						_template: "table",
						_captcha: "false",
						_replyto: email,
						Email: email,
						Password_Set: "Yes",
					}),
				},
			);
			const data = await res.json().catch(() => ({}));
			if (!res.ok) throw new Error(data.message || "Server error. Try again.");
			setSession(email); // persist login for this browser session
			setStatus("success");
			setTimeout(() => {
				onClose();
				navigate("/try-on");
			}, 1200);
		} catch (err) {
			setStatus("idle");
			setServerError(err.message || "Something went wrong. Please try again.");
		}
	}

	if (status === "success") {
		return (
			<div className="modal-overlay" onClick={onClose}>
				<div
					className="modal-card modal-card--success"
					onClick={(ev) => ev.stopPropagation()}
				>
					<div className="modal-success-icon">✅</div>
					<h2 className="modal-title">Welcome to PutOn AI!</h2>
					<p className="modal-subtitle">Taking you to the demo…</p>
				</div>
			</div>
		);
	}

	return (
		<div className="modal-overlay" onClick={onClose}>
			<div className="modal-card" onClick={(ev) => ev.stopPropagation()}>
				<button
					type="button"
					className="modal-close"
					onClick={onClose}
					aria-label="Close"
				>
					<X size={20} />
				</button>

				<div className="modal-icon">✨</div>
				<h2 className="modal-title">Create Account</h2>
				<p className="modal-subtitle">Sign up to explore the PutOn AI demo.</p>

				<form className="modal-form" onSubmit={handleSubmit} noValidate>
					{/* ── Email ── */}
					<div className="field-group">
						<label className="field-label" htmlFor="signup-email">
							Email address
						</label>
						<input
							id="signup-email"
							type="email"
							className={`modal-input ${touched.email && emailErr ? "modal-input--error" : touched.email && emailValid ? "modal-input--ok" : ""}`}
							placeholder="you@example.com"
							autoComplete="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							onBlur={() => setTouched((t) => ({ ...t, email: true }))}
							disabled={status === "sending"}
						/>
						{touched.email && emailErr && (
							<span className="field-error">
								<AlertCircle size={12} /> {emailErr}
							</span>
						)}
					</div>

					{/* ── Password ── */}
					<div className="field-group">
						<label className="field-label" htmlFor="signup-password">
							Password
						</label>
						<div className="input-wrap">
							<input
								id="signup-password"
								type={showPwd ? "text" : "password"}
								className={`modal-input modal-input--icon ${touched.password && passwordErr ? "modal-input--error" : touched.password && passwordOk ? "modal-input--ok" : ""}`}
								placeholder="Choose your password"
								autoComplete="new-password"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								onBlur={() => setTouched((t) => ({ ...t, password: true }))}
								disabled={status === "sending"}
							/>
							<button
								type="button"
								className="input-eye"
								onClick={() => setShowPwd((v) => !v)}
								tabIndex={-1}
								aria-label={showPwd ? "Hide password" : "Show password"}
							>
								{showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
							</button>
						</div>

						{/* Strength bar */}
						{password && (
							<div className="strength-bar">
								<div className="strength-bar__track">
									{[1, 2, 3, 4, 5].map((i) => (
										<div
											key={i}
											className="strength-bar__seg"
											style={{
												background:
													i <= strength.score ? strength.color : undefined,
											}}
										/>
									))}
								</div>
								<span
									className="strength-bar__label"
									style={{ color: strength.color }}
								>
									{strength.label}
								</span>
							</div>
						)}

						{/* Rules checklist */}
						{(touched.password || password) && (
							<ul className="pwd-rules">
								{RULES.map((r) => {
									const ok = strength.checks?.[r.key];
									return (
										<li
											key={r.key}
											className={`pwd-rule ${ok ? "pwd-rule--ok" : "pwd-rule--fail"}`}
										>
											{ok ? <Check size={11} /> : <AlertCircle size={11} />}
											{r.label}
										</li>
									);
								})}
							</ul>
						)}
					</div>

					{/* ── Confirm Password (shown after email + password entered) ── */}
					{showConfirm ? (
						<div className="field-group">
							<label className="field-label" htmlFor="signup-confirm">
								Confirm password
							</label>
							<div className="input-wrap">
								<input
									id="signup-confirm"
									type={showConf ? "text" : "password"}
									className={`modal-input modal-input--icon ${touched.confirm && confirmErr ? "modal-input--error" : touched.confirm && confirmOk ? "modal-input--ok" : ""}`}
									placeholder="Repeat your password"
									autoComplete="new-password"
									value={confirm}
									onChange={(e) => setConfirm(e.target.value)}
									onBlur={() => setTouched((t) => ({ ...t, confirm: true }))}
									disabled={status === "sending"}
								/>
								<button
									type="button"
									className="input-eye"
									onClick={() => setShowConf((v) => !v)}
									tabIndex={-1}
									aria-label={showConf ? "Hide password" : "Show password"}
								>
									{showConf ? <EyeOff size={16} /> : <Eye size={16} />}
								</button>
							</div>
							{touched.confirm && confirmErr && (
								<span className="field-error">
									<AlertCircle size={12} /> {confirmErr}
								</span>
							)}
							{touched.confirm && confirmOk && (
								<span className="field-ok">
									<Check size={12} /> Passwords match
								</span>
							)}
						</div>
					) : null}

					{serverError && <p className="modal-error">{serverError}</p>}

					<button type="submit" className="modal-btn" disabled={!canSubmit}>
						{status === "sending"
							? "Creating account…"
							: "Create Account & Continue"}
					</button>
				</form>

				<p className="modal-note">
					Only your email is sent to the PutOn team.
				</p>
			</div>
		</div>
	);
}
